import React from "react";
import SEOHead from "../components/tools/SEOHead";
import MarketingShell, {
  Arrow,
  bookingHref,
  useMarketingCopy,
} from "./MarketingShell";
import marketingRoutes from "./marketingRoutes.json";
import SharePageLink from "./SharePageLink";
import BusinessOutcomes from "./BusinessOutcomes";
import narrative from "./siteNarrativeCopy.json";

export default function BusinessPage() {
  const { routes, content, copy, language } = useMarketingCopy();
  const business = routes.business;
  const searchAnswers = {
    en: [
      { question: "How do AI agents use company data?", answer: "We connect approved CRM, database, email, meeting, and document sources. The agent uses that context to answer questions and complete scoped work with source references." },
      { question: "What business work can YouWo automate?", answer: "Start with a repeatable bottleneck such as client reporting, sales research, internal questions, or order allocation. We set the goal and review points before execution." },
      { question: "How do you measure AI ROI?", answer: "We establish a baseline, then compare turnaround time, manual hours, quality, and relevant revenue or margin measures after the pilot." },
    ],
    zh: [
      { question: "AI 智能体如何使用企业数据？", answer: "我们在授权范围内连接 CRM、数据库、邮件、会议和文档。智能体利用这些背景回答问题、完成限定任务，并提供来源。" },
      { question: "YouWo 可以自动化哪些业务工作？", answer: "从客户报告、销售研究、内部问答或订单分配等重复瓶颈开始，先明确目标和审阅节点，再执行。" },
      { question: "如何衡量 AI 的投入产出？", answer: "先确定基线，再比较试点前后的周转时间、人工工时、质量及相关收入或利润指标。" },
    ],
    ja: [
      { question: "AI エージェントは企業データをどう使いますか？", answer: "承認された CRM、データベース、メール、会議、文書を接続します。出典を示しながら、質問への回答や範囲を定めた業務に活用します。" },
      { question: "YouWo はどんな業務を自動化できますか？", answer: "顧客向けレポート、営業調査、社内の質問対応、受注配分など、繰り返す課題から始めます。実行前に目標と確認点を決めます。" },
      { question: "AI の投資対効果はどう測りますか？", answer: "基準値を定め、試験導入の前後で所要時間、手作業の時間、品質、関連する売上や利益を比較します。" },
    ],
  }[language] ?? [];
  return (
    <MarketingShell active="business">
      <SEOHead
        title={language === "en" ? "AI Agents for Business Growth & Workflow Automation | YouWo" : `YouWo — ${business.label}`}
        description={language === "en" ? "Connect CRM, databases, email, meetings, and documents to AI agents that automate client reports and business workflows. Start with measurable outcomes." : copy.seo.description}
        canonicalUrl="https://youwo.ai/business"
        ogImage="https://youwo.ai/landing/display.png"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: business.label,
          provider: { "@type": "Organization", name: "YouWo" },
          description: language === "en" ? "Connect company data to AI agents that automate reports and workflows with measurable outcomes." : copy.seo.description,
          url: "https://youwo.ai/business",
        }}
      />
      <main id="main-content" tabIndex={-1}>
        <section
          className="quant-wrap destination-hero"
          aria-labelledby="business-title"
        >
          <p className="quant-eyebrow">{business.label}</p>
          <h1 id="business-title">
            {business.title}
            <br />
            <em>{business.accent}</em>
          </h1>
          <p className="quant-lead">{business.description}</p>
          <SharePageLink />
        </section>
        <BusinessOutcomes />
        <section
          className="quant-wrap business-paths"
          aria-label={content.offers.label}
        >
          {content.offers.items.map((item, index) => (
            <a
              className="business-path"
              href={marketingRoutes.paths[index]}
              key={item.title}
            >
              <span>0{index + 1}</span>
              <div>
                <h2>{item.title}</h2>
                <p>{item.output}</p>
              </div>
              <Arrow diagonal />
            </a>
          ))}
        </section>
        <div className="quant-wrap business-case-link"><a className="quant-text-link" href="/blog#case-studies">{(narrative[language] ?? narrative.en).home.allCases}<Arrow /></a></div>
        <div className="quant-wrap business-details">
          <details className="marketing-disclosure">
            <summary>
              {business.detail}
              <span aria-hidden="true">+</span>
            </summary>
            <ol className="brief-sequence">
              {content.capabilities.steps.map((step, index) => (
                <li key={step.title}>
                  <span>0{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
            <a className="quant-text-link" href="/product">
              {content.capabilities.link}
              <Arrow />
            </a>
          </details>
          <a href="/research/memory-benchmarks" className="quant-text-link">
            {business.proof}
            <Arrow />
          </a>
        </div>
        <section className="quant-wrap business-search-answers" aria-label={language === "zh" ? "常见问题" : language === "ja" ? "よくある質問" : "Common questions"}>
          {searchAnswers.map(item => <details key={item.question}><summary>{item.question}</summary><p>{item.answer}</p></details>)}
        </section>
        <section id="start" className="marketing-contact">
          <div className="quant-wrap">
            <h2>{content.contact.title}</h2>
            <a
              className="quant-button"
              href={bookingHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {business.cta}
              <Arrow diagonal />
            </a>
          </div>
        </section>
      </main>
    </MarketingShell>
  );
}
